import React, { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import type { AppDispatch } from 'state/store';
import {
  layerAdded,
  layerRemoved,
  layerVisibilityToggled,
  layerDisplayNameUpdated,
  layerOpacityUpdated,
  layerHighlightToggled,
  layerLineWidthUpdated,
  layerLineStyleUpdated,
  layerVisualizationSettingsSaved,
  modalClosed,
  modalOpened,
  layerSettingsToggled,
  layerSettingsClosed,
  advancedSettingsToggled,
  buildActiveLayerId,
  ActiveDefectLayerId,
  selectActiveDefectLayers,
  selectActiveModal,
  selectExpandedLayerId,
  selectAdvancedSettingsLayerId,
  selectIsActivatingLayers,
  selectLayerActivation,
  layerActivationStarted,
  layerActivationSucceeded,
  layerActivationFailed,
  layerActivationErrorCleared,
} from 'state/surveyDataManager';

import { AvailableViewSummary } from 'domain/surveyDataManager/contracts/availableViews';
import { RecipeComponentFailure } from 'domain/surveyDataManager/contracts/recipes';
import { expandRecipe } from 'domain/surveyDataManager/recipes/expandRecipe';
import { mergeScannerMockCatalogue } from 'domain/surveyDataManager/api/mergeScannerMockCatalogue';
import { mergeDefectOverlayCatalogDefects } from 'domain/surveyDataManager/defectOverlays/mergeDefectOverlayCatalogs';
import { mergeDefectOverlayViews } from 'domain/surveyDataManager/defectOverlays/mergeDefectOverlayViews';
import {
  surveyDataManagerQueryKeys,
  useAvailableViews,
  useDefects,
} from 'domain/surveyDataManager/hooks';
import { clearAllPersistedSessionLayers } from 'domain/surveyDataManager/persistence/activeLayersSessionStorage';
import type { ActiveLayerVisualizationSettings } from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';

import { AddLayerWizard } from '../../components/AddLayerWizard';
import AvailableViewsPanel from '../../components/AvailableViewsPanel';
import type { CompletedWizardLayer } from '../../components/AddLayerWizard/wizardLogic';
import InlineErrorBanner from '../../components/InlineErrorBanner';
import { activateActiveLayers } from './activateActiveLayers';
import {
  activateRecipeView,
  deactivateRecipeView,
} from '../../recipeVisibility';
import {
  findCatalogMeasurement,
  seedSettingsFromVisualizationRule,
} from 'domain/surveyDataManager/visualization/activeLayerVisualizationSettings';

import './HomePage.scss';

interface HomePageProps {
  readonly refreshData?: boolean;
  readonly refreshRequestId?: number;
  onRefreshSettled?: () => void;
}

function HomePage({
  refreshData = false,
  refreshRequestId = 0,
  onRefreshSettled,
}: HomePageProps): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();

  const defectsQuery = useDefects({}, refreshData);
  const availableViewsQuery = useAvailableViews(refreshData);
  const activeLayers = useSelector(selectActiveDefectLayers);
  const activeModal = useSelector(selectActiveModal);
  const expandedLayerId = useSelector(selectExpandedLayerId);
  const advancedSettingsLayerId = useSelector(selectAdvancedSettingsLayerId);
  const isAddingLayer = useSelector(selectIsActivatingLayers);
  const layerActivation = useSelector(selectLayerActivation);
  const isAddLayerWizardOpen = activeModal === 'addLayer';
  const catalogDefects = mergeDefectOverlayCatalogDefects(
    mergeScannerMockCatalogue(defectsQuery.data?.defects ?? []),
  );
  const availableViews = mergeDefectOverlayViews({
    views: availableViewsQuery.data?.views ?? [],
  }).views;
  const isCatalogueLoading =
    defectsQuery.isLoading || (!defectsQuery.data && defectsQuery.isFetching);

  const activeLayersRef = useRef(activeLayers);
  const catalogDefectsRef = useRef(catalogDefects);
  activeLayersRef.current = activeLayers;
  catalogDefectsRef.current = catalogDefects;

  useEffect(() => {
    if (refreshRequestId === 0 || !refreshData) {
      return;
    }

    queryClient
      .invalidateQueries({ queryKey: surveyDataManagerQueryKeys.root })
      .finally(() => {
        onRefreshSettled?.();
      });
  }, [onRefreshSettled, queryClient, refreshData, refreshRequestId]);

  // Recipes/layers must not survive refresh or a new session — drop any leftover storage.
  useEffect(() => {
    clearAllPersistedSessionLayers();
  }, []);

  const handleOpenLayerBuilder = (): void => {
    if (isAddingLayer || isAddLayerWizardOpen) {
      return;
    }
    dispatch(layerActivationErrorCleared());
    dispatch(modalOpened('addLayer'));
  };

  const handleWizardCancel = () => {
    if (isAddingLayer) {
      return;
    }
    dispatch(layerActivationErrorCleared());
    dispatch(modalClosed());
  };

  const handleWizardComplete = async (
    layers: readonly CompletedWizardLayer[],
  ): Promise<void> => {
    if (layers.length === 0) {
      return;
    }

    dispatch(layerActivationStarted({ kind: 'wizard' }));
    try {
      await activateActiveLayers({
        layers,
        dispatch,
        queryClient,
        existingLayerIds: new Set(activeLayers.map((layer) => layer.id)),
      });

      for (const layer of layers) {
        if (!layer.colourOverride) {
          continue;
        }
        const measurement = findCatalogMeasurement(
          catalogDefects,
          layer.defectSlug,
          layer.measurementSlug,
        );
        if (!measurement) {
          continue;
        }
        const defaults = measurement.colorScheme;
        const coloursChanged =
          layer.colourOverride.colorLow !== defaults.min ||
          layer.colourOverride.colorHigh !== defaults.max;
        if (!coloursChanged) {
          continue;
        }
        dispatch(
          layerVisualizationSettingsSaved({
            id: buildActiveLayerId(layer),
            settings: {
              ...seedSettingsFromVisualizationRule(measurement),
              colorLow: layer.colourOverride.colorLow,
              colorHigh: layer.colourOverride.colorHigh,
            },
          }),
        );
      }

      dispatch(layerActivationSucceeded());
      dispatch(modalClosed());
    } catch (error) {
      dispatch(
        layerActivationFailed({
          message:
            error instanceof Error
              ? error.message
              : 'Could not add the selected layer(s).',
        }),
      );
      throw error;
    }
  };

  const handleActivateRecipe = async (
    view: AvailableViewSummary,
  ): Promise<readonly RecipeComponentFailure[]> => {
    const catalog = catalogDefectsRef.current;
    const expansion = expandRecipe(
      view,
      catalog,
      new Set(),
      buildActiveLayerId,
    );

    try {
      await activateRecipeView({
        view,
        catalogDefects: catalog,
        activeLayers: activeLayersRef.current,
        dispatch,
        queryClient,
      });
      return expansion.failures;
    } catch {
      return expansion.failures;
    }
  };

  const handleDeactivateRecipe = (recipeId: string): void => {
    const view = availableViews.find((entry) => entry.id === recipeId);
    if (!view) {
      return;
    }
    deactivateRecipeView({
      view,
      catalogDefects: catalogDefectsRef.current,
      dispatch,
    });
  };

  const handleToggleLayerVisibility = (layerId: ActiveDefectLayerId): void => {
    dispatch(layerVisibilityToggled({ id: layerId }));
  };

  const handleRemoveLayer = (layerId: ActiveDefectLayerId): void => {
    dispatch(layerRemoved({ id: layerId }));
    if (expandedLayerId === layerId) {
      dispatch(layerSettingsClosed());
    }
  };

  const handleToggleSettings = (layerId: ActiveDefectLayerId): void => {
    dispatch(layerSettingsToggled(layerId));
  };

  const handleDisplayNameChange = (
    layerId: ActiveDefectLayerId,
    displayName: string,
  ): void => {
    dispatch(layerDisplayNameUpdated({ id: layerId, displayName }));
  };

  const handleOpacityChange = (layerId: ActiveDefectLayerId, opacity: number): void => {
    dispatch(layerOpacityUpdated({ id: layerId, opacity }));
  };

  const handleHighlightToggle = (layerId: ActiveDefectLayerId): void => {
    dispatch(layerHighlightToggled({ id: layerId }));
  };

  const handleLineWidthChange = (layerId: ActiveDefectLayerId, lineWidth: number): void => {
    dispatch(layerLineWidthUpdated({ id: layerId, lineWidth }));
  };

  const handleLineStyleChange = (layerId: ActiveDefectLayerId, lineStyle: string): void => {
    dispatch(layerLineStyleUpdated({ id: layerId, lineStyle }));
  };

  const handleToggleAdvancedSettings = (layerId: ActiveDefectLayerId): void => {
    dispatch(advancedSettingsToggled(layerId));
  };

  const handleVisualizationSettingsSave = (
    layerId: ActiveDefectLayerId,
    settings: ActiveLayerVisualizationSettings,
  ): void => {
    dispatch(layerVisualizationSettingsSaved({ id: layerId, settings }));
  };

  return (
    <div className="sdm-home-page">
      {/* <p className="sdm-home-page__subtitle">
        Turn on built-in recipes, or add a personal layer with the step-by-step wizard.
      </p> */}

      {layerActivation.status === 'error' && layerActivation.message ? (
        <InlineErrorBanner
          title="Couldn't activate layers"
          error={{ status: 0, message: layerActivation.message }}
          onRetry={() => dispatch(layerActivationErrorCleared())}
        />
      ) : null}

      <section className="sdm-home-page__layers">
        {isAddLayerWizardOpen ? (
          <AddLayerWizard
            isOpen
            refreshData={refreshData}
            isSubmitting={isAddingLayer}
            activeLayers={activeLayers}
            onCancel={handleWizardCancel}
            onComplete={handleWizardComplete}
          />
        ) : (
          <AvailableViewsPanel
            refreshData={refreshData}
            layers={activeLayers}
            defects={catalogDefects}
            expandedLayerId={expandedLayerId}
            advancedSettingsLayerId={advancedSettingsLayerId}
            isAddLayerOpen={false}
            isAddLayerLoading={isCatalogueLoading}
            isAddingLayer={isAddingLayer}
            isCatalogueLoading={isCatalogueLoading}
            onAddLayer={handleOpenLayerBuilder}
            onActivateRecipe={handleActivateRecipe}
            onDeactivateRecipe={handleDeactivateRecipe}
            onToggleVisibility={handleToggleLayerVisibility}
            onRemove={handleRemoveLayer}
            onToggleSettings={handleToggleSettings}
            onToggleAdvancedSettings={handleToggleAdvancedSettings}
            onDisplayNameChange={handleDisplayNameChange}
            onOpacityChange={handleOpacityChange}
            onHighlightToggle={handleHighlightToggle}
            onLineWidthChange={handleLineWidthChange}
            onLineStyleChange={handleLineStyleChange}
            onVisualizationSettingsSave={handleVisualizationSettingsSave}
          />
        )}
      </section>
    </div>
  );
}

export default HomePage;
